import {
  type CascoCampaign,
  type CascoCriteria,
  type GeneratorKey,
  leadGenerationCampaignsPartialUpdateMutation,
  leadGenerationCampaignsRetrieveQueryKey
} from '@crm/api'
import { criteriaFieldsByGenerator } from '@crm/features/lead-generation/utils'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import { Button, Card, Empty, Flex, Form, notification } from 'antd'
import { useEffect } from 'react'

import { GeneratorTag } from '../GeneratorTag/GeneratorTag'

const FORM_ID = 'lead-generation-criteria-form'

type Props = {
  campaign?: CascoCampaign
  generatorKey: GeneratorKey
  isLoading?: boolean
}

export const CampaignCriteriaCard = ({
  campaign,
  generatorKey,
  isLoading
}: Props) => {
  const { t } = useLocales()
  const [form] = Form.useForm<CascoCriteria>()

  const { invalidateQueries } = useInvalidatePredicateQueries()

  const CriteriaFields = criteriaFieldsByGenerator[generatorKey]

  useEffect(() => {
    if (campaign?.criteria) {
      const { excluded_casco_packages, ...rest } = campaign.criteria

      // The API types this array as nullable, but antd's setFieldsValue only
      // accepts undefined for an absent list value.
      form.setFieldsValue({
        ...rest,
        excluded_casco_packages: excluded_casco_packages ?? undefined
      })
    }
  }, [campaign, form])

  const { mutate, isPending } = useMutation({
    ...leadGenerationCampaignsPartialUpdateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([
        leadGenerationCampaignsRetrieveQueryKey({
          path: { generator_key: generatorKey }
        })
      ])

      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  })

  const onSubmit = (criteria: CascoCriteria) =>
    mutate({ path: { generator_key: generatorKey }, body: { criteria } })

  return (
    <Card
      loading={isLoading}
      title={
        <Flex align="center" gap={8} wrap>
          {t('lead_generation.selection_criteria')}
          <Flex align="center" gap={4}>
            {t('lead_generation.generator_specific')}
            <GeneratorTag generatorKey={generatorKey} />
          </Flex>
        </Flex>
      }
      extra={
        CriteriaFields && (
          <Button htmlType="submit" form={FORM_ID} loading={isPending}>
            {t('buttons.save')}
          </Button>
        )
      }
    >
      {CriteriaFields ? (
        <Form
          id={FORM_ID}
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          requiredMark={false}
        >
          <CriteriaFields />
        </Form>
      ) : (
        <Empty description={t('lead_generation.no_criteria_form')} />
      )}
    </Card>
  )
}
